import React, { useEffect, useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  CircularProgress, 
  Paper, 
  Typography, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Chip 
} from '@mui/material';
import { collection, query, getDocs, doc, setDoc, where, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase-config';
import Collections from '../collections';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteDoc } from "firebase/firestore";
import { EditOutlined } from '@mui/icons-material';
import { Switch } from "@mui/material";

const AddCategoryModal = ({ open, onClose }) => {
  const [category, setCategory] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [updateClicked, setUpdateClicked] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(false);
  const [togglingCategories, setTogglingCategories] = useState({}); // Track loading states per category

  const fetchCategories = async () => {
    const categoryQuery = query(collection(db, Collections.INVENTORY_CATEGORY));
    const querySnapshot = await getDocs(categoryQuery);
    const categoriesList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setCategories(categoriesList);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const toggleCategoryStatus = async (cat) => {
    // Start loading for this specific category
    setTogglingCategories(prev => ({
      ...prev,
      [cat.id]: true
    }));

    // Optimistically update the UI
    setCategories(prevCategories => 
      prevCategories.map(c => {
        if (c.id === cat.id) {
          const currentStatus = c.isActive !== undefined ? c.isActive : true;
          return { ...c, isActive: !currentStatus };
        }
        return c;
      })
    );

    try {
      // If isActive doesn't exist, default to true
      // So when toggling from undefined (true), set to false
      const currentStatus = cat.isActive !== undefined ? cat.isActive : true;
      const newStatus = !currentStatus;
      
      await updateDoc(
        doc(db, Collections.INVENTORY_CATEGORY, cat.id),
        { isActive: newStatus }
      );
      
      // Refresh to ensure data consistency (optional)
      fetchCategories();
    } catch (err) {
      console.error("Failed to toggle category", err);
      setError("Failed to toggle category status");
      
      // Revert optimistic update on error
      setCategories(prevCategories => 
        prevCategories.map(c => {
          if (c.id === cat.id) {
            return { ...c, isActive: cat.isActive !== undefined ? cat.isActive : true };
          }
          return c;
        })
      );
    } finally {
      // Stop loading for this category
      setTogglingCategories(prev => ({
        ...prev,
        [cat.id]: false
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);

    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setLoading(true);
    try {
      // 1. Get all items under this category
      const itemsQuery = query(
        collection(db, Collections.INVENTORY_ITEMS),
        where("categoryId", "==", categoryId)
      );
      const itemsSnapshot = await getDocs(itemsQuery);

      // 2. Delete all items
      const deletePromises = itemsSnapshot.docs.map((item) =>
        deleteDoc(doc(db, Collections.INVENTORY_ITEMS, item.id))
      );
      await Promise.all(deletePromises);

      // 3. Delete category image
      await deleteObject(
        ref(storage, `categories/${categoryId}.jpg`)
      ).catch(() => { });

      // 4. Delete category
      await deleteDoc(
        doc(db, Collections.INVENTORY_CATEGORY, categoryId)
      );

      fetchCategories();
    } catch (err) {
      console.error("Delete failed", err);
      setError("Failed to delete category");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!category) {
      setError('Please provide a category name');
      return;
    }

    if (!isEditing && !image) {
      setError('Please select an image for the category');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        // EDIT EXISTING CATEGORY
        const categoryToUpdate = categories.find(cat => cat.id === editingCategoryId);

        if (!categoryToUpdate) {
          throw new Error('Category to update not found in local state');
        }

        const categoryData = {
          ...categoryToUpdate, // Keep all existing properties
          category, // Update the name
          updatedAt: new Date().toISOString() // Add update timestamp
        };

        if (image) {
          // Delete old image if it exists
          if (categoryToUpdate.image) {
            const oldImageRef = ref(storage, `categories/${editingCategoryId}.jpg`);
            await deleteObject(oldImageRef).catch(console.error);
          }

          // Upload new image
          const newImageRef = ref(storage, `categories/${editingCategoryId}.jpg`);
          await uploadBytes(newImageRef, image);
          categoryData.image = await getDownloadURL(newImageRef);
        }

        await setDoc(doc(db, Collections.INVENTORY_CATEGORY, editingCategoryId), categoryData);
      } else {
        // ADD NEW CATEGORY
        const newDocRef = doc(collection(db, Collections.INVENTORY_CATEGORY));
        const imageRef = ref(storage, `categories/${newDocRef.id}.jpg`);
        await uploadBytes(imageRef, image);
        const downloadURL = await getDownloadURL(imageRef);

        const categoryData = {
          id: newDocRef.id,
          category,
          image: downloadURL,
          isActive: true, // Default to active for new categories
          createdAt: new Date().toISOString(),
          updatedAt: null
        };

        await setDoc(newDocRef, categoryData);
      }

      // Reset form fields
      setCategory('');
      setImage(null);
      setImagePreview(null);
      setError('');
      onClose();
      fetchCategories();
    } catch (err) {
      console.error("Error adding/updating category: ", err);
      setError(err.message.includes('not found')
        ? 'Category not found - please refresh and try again'
        : 'Failed to add/update category');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (category) => {
    setCategory(category.category);
    setImagePreview(category.image);
    setEditingCategoryId(category.id);
    setIsEditing(true);
    setUpdateClicked(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(!isEditing);
    setCategory('');
    setImage(null);
    setImagePreview(null);
    setEditingCategoryId(null);
    setUpdateClicked(false);
  };

  const renderCategoryList = () => (
    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {categories.map(cat => {
        // Default to true if isActive doesn't exist
        const isActive = cat.isActive !== undefined ? cat.isActive : true;
        const isToggling = togglingCategories[cat.id];
        
        return (
          <Paper
            key={cat.id}
            sx={{
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              m: 1,
              opacity: isToggling ? 0.7 : 1,
              transition: 'opacity 0.3s ease'
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              {!isActive && (
                <Chip 
                  label="Disabled" 
                  size="small" 
                  color="error" 
                  sx={{ opacity: isToggling ? 0.5 : 1 }}
                />
              )}
              <Box sx={{ position: 'relative' }}>
                <img
                  src={cat.image}
                  alt={cat.category}
                  style={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: 8,
                    opacity: isToggling ? 0.5 : 1
                  }}
                />
                {isToggling && (
                  <CircularProgress 
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </Box>
              <Typography 
                fontWeight={600}
                sx={{ opacity: isToggling ? 0.5 : 1 }}
              >
                {cat.category}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton 
                onClick={() => handleEditClick(cat)}
                disabled={isToggling}
                sx={{ opacity: isToggling ? 0.5 : 1 }}
              >
                <EditOutlined />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => setDeleteTarget(cat)}
                disabled={isToggling}
                sx={{ opacity: isToggling ? 0.5 : 1 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
              <Box sx={{ position: 'relative', width: 42, height: 26 }}>
                {isToggling ? (
                  <CircularProgress 
                    size={20}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-10px',
                      marginLeft: '-10px',
                    }}
                  />
                ) : (
                  <Switch
                    checked={isActive}
                    onChange={() => toggleCategoryStatus(cat)}
                    color="success"
                    disabled={isToggling}
                  />
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ position: 'relative' }}>
        {isEditing ? 'Edit Category' : 'Add Category'}
        <Button
          onClick={() => {
            if (updateClicked) {
              setIsEditing(true);
            } else {
              setIsEditing(!isEditing);
            }
            setUpdateClicked(false);
          }}
          variant="outlined"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          {isEditing ? 'Add Category' : 'Edit Categories'}
        </Button>
      </DialogTitle>

      <DialogContent>
        {(!isEditing || updateClicked) ? (
          <form onSubmit={handleSubmit}>
            <TextField
              label="Category Name"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              margin="normal"
              disabled={loading}
            />
            <input
              type="file"
              onChange={handleImageChange}
              style={{ marginTop: '20px' }}
              accept="image/*"
              disabled={loading}
            />
            {imagePreview && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={imagePreview}
                  alt="Category Preview"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '200px',
                    borderRadius: '8px'
                  }}
                />
              </Box>
            )}
            {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
              <Button
                onClick={() => {
                  onClose();
                  setCategory('');
                  setImage(null);
                  setImagePreview(null);
                  setEditingCategoryId(null);
                  setUpdateClicked(false);
                }}
                variant="outlined"
                color="secondary"
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </form>
        ) : (
          <div>
            {renderCategoryList()}
            <Button
              onClick={handleCancelEdit}
              variant="outlined"
              color="secondary"
              sx={{ mt: 2 }}
              fullWidth
              disabled={Object.values(togglingCategories).some(Boolean)}
            >
              Cancel Edit
            </Button>
          </div>
        )}

        {deleteTarget && (
          <Paper
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'error.light'
            }}
          >
            <Typography>
              Delete <b>{deleteTarget.category}</b> and all items under it?
            </Typography>
            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
              <Button 
                onClick={() => setDeleteTarget(null)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => {
                  handleDeleteCategory(deleteTarget.id);
                  setDeleteTarget(null);
                }}
                disabled={loading}
              >
                Delete
              </Button>
            </Box>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddCategoryModal;